import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Download, FileCheck2 } from 'lucide-react';
import {
  fetchInspeccionesPeriodo, fetchDiasSinOperacion, marcarDiaSinOperacion, anularDiaSinOperacion,
  registrarReporte, fetchNombres,
} from 'services/adminService';
import { fetchArbolesEstado, fetchEvaluacionesArboles } from 'services/arbolesService';
import { fetchLineasEstado, fetchEventosLineas } from 'services/cablesService';
import { lineasParaReporte } from 'lib/cables';
import {
  coberturaPorDia, resumenCobertura, formatoFolio, fechaCorta, fechaHoraCorta, idsUsuarios, plural,
} from 'lib/auditoria';
import { generarCsvAuditoria, sha256Hex } from 'lib/auditoriaCsv';
import { arbolesParaReporte } from 'lib/arboles';
import { flattenHallazgos } from 'lib/hallazgos';
import { etiquetaInstalacion, checklistDe } from 'lib/auditoriaInstalaciones';
import { arbolesControl, cablesControl, resumenDiario } from 'lib/reporteControl';
import { hoyCR } from 'lib/fechaCR';
import { useAuth } from 'contexts/AuthContext';
import AppShell from 'components/AppShell';
import SelectorInstalacion, { useInstalacion } from 'components/SelectorInstalacion';
import Cargando from 'components/Cargando';
import MotivoDialog from 'components/MotivoDialog';
import PeriodoSelector, { periodoInicial, ATAJOS_REPORTE } from 'components/PeriodoSelector';
import Button from 'components/ui/Button';
import ErrorState from 'components/ui/ErrorState';
import BloqueDiario from './BloqueDiario';
import BloqueArboles from './BloqueArboles';
import BloqueCables from './BloqueCables';
import { mensajeError } from 'lib/errores';

// jspdf solo baja al generar el reporte.
const cargarPdf = () => import('lib/auditoriaPdf');

export const ERROR_GENERAR = 'No se pudo generar el reporte. Intente de nuevo.';

// Resumen del período en palabras normales.
export function textoResumen(r) {
  const partes = [
    plural(r.inspecciones, 'inspección hecha', 'inspecciones hechas'),
    plural(r.sinRegistro, 'día sin inspección', 'días sin inspección'),
  ];
  if (r.sinOperacion) partes.push(plural(r.sinOperacion, 'día que no se operó', 'días que no se operó'));
  return `${partes.join(', ')}.`;
}

function descargar(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// Lectura que no tumba la pantalla: null si falla (el bloque y el reporte lo dicen).
const suave = (p) => p.then((v) => ({ v, e: null })).catch((e) => ({ v: null, e: e ? mensajeError(e) : null }));

export default function Reportes() {
  const { role, user } = useAuth();
  const puedeMarcar = role === 'admin' || role === 'editor';
  const esAdmin = role === 'admin';
  const hoy = hoyCR();
  const [instalacion, setInstalacion] = useInstalacion();

  const [periodo, setPeriodo] = useState(() => periodoInicial('mes_anterior'));
  const [rows, setRows] = useState([]);
  const [sinOp, setSinOp] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  // Árboles, cables, evaluaciones y eventos no dependen del período: se leen una vez.
  const [fichas, setFichas] = useState(null);
  const [dialogo, setDialogo] = useState(null); // { tipo: 'marcar'|'anular', dia }
  const [generando, setGenerando] = useState(false);
  const [errorReporte, setErrorReporte] = useState('');
  const [reporte, setReporte] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [r, s] = await Promise.all([
        fetchInspeccionesPeriodo({ ...periodo, instalacion }),
        fetchDiasSinOperacion({ ...periodo, instalacion }),
      ]);
      setRows(r);
      setSinOp(s);
    } catch (e) {
      setError(e);
    } finally {
      setCargando(false);
    }
  }, [periodo, instalacion]);

  useEffect(() => { cargar(); setReporte(null); setErrorReporte(''); }, [cargar]);

  useEffect(() => {
    let vivo = true;
    Promise.all([suave(fetchArbolesEstado()), suave(fetchLineasEstado()), suave(fetchEvaluacionesArboles()), suave(fetchEventosLineas())])
      .then(([arboles, lineas, evaluaciones, eventos]) => { if (vivo) setFichas({ arboles, lineas, evaluaciones, eventos }); });
    return () => { vivo = false; };
  }, []);

  const dias = useMemo(() => coberturaPorDia({
    inspecciones: rows, diasSinOperacion: sinOp, desde: periodo.desde, hasta: periodo.hasta, hoy,
  }), [rows, sinOp, periodo, hoy]);
  const resumen = useMemo(() => resumenCobertura(dias), [dias]);
  const diario = useMemo(() => resumenDiario(dias), [dias]);
  const hallazgos = useMemo(() => flattenHallazgos(rows), [rows]);
  const arbolesCtl = useMemo(() => (fichas ? arbolesControl({
    arboles: arbolesParaReporte({ arboles: fichas.arboles.v, inspecciones: rows, instalacion, hoy }),
    evaluaciones: fichas.evaluaciones.v, desde: periodo.desde, hasta: periodo.hasta,
  }) : null), [fichas, rows, instalacion, hoy, periodo]);
  const cablesCtl = useMemo(() => (fichas ? cablesControl({
    lineas: lineasParaReporte({ lineas: fichas.lineas.v, inspecciones: rows, instalacion, hoy }),
    eventos: fichas.eventos.v, desde: periodo.desde, hasta: periodo.hasta,
  }) : null), [fichas, rows, instalacion, hoy, periodo]);
  const enValidacion = !!checklistDe(instalacion)?.pendiente;

  const confirmarDialogo = async (motivo) => {
    if (dialogo.tipo === 'marcar') {
      const nueva = await marcarDiaSinOperacion(dialogo.dia.fecha, motivo, instalacion);
      setSinOp((s) => [...s, nueva || { id: `tmp-${dialogo.dia.fecha}`, fecha: dialogo.dia.fecha, motivo, anulada: false, instalacion }]);
    } else {
      const fila = await anularDiaSinOperacion(dialogo.dia.sinOperacion.id, motivo);
      setSinOp((s) => s.map((x) => (x.id === fila.id ? { ...x, ...fila } : x)));
    }
    setReporte(null);
  };

  // El número de reporte y la hora salen del servidor (cables_registrar_reporte,
  // que guarda también el código de control del CSV). Sin número no hay
  // documentos: si falla, no se descarga nada.
  const generar = async () => {
    setGenerando(true);
    setErrorReporte('');
    setReporte(null);
    try {
      const nombres = await fetchNombres([...idsUsuarios(rows, sinOp), user?.id].filter(Boolean));
      const arboles = arbolesCtl ? arbolesCtl.filas : null;
      const lineas = cablesCtl ? cablesCtl.filas : null;
      const comun = {
        inspecciones: rows, dias, desde: periodo.desde, hasta: periodo.hasta, instalacion, nombres, marcasSinOperacion: sinOp,
        arboles, lineas, diario,
        evaluaciones: arbolesCtl?.evaluacionesDisponibles ? arbolesCtl.evaluacionesPeriodo : null,
        eventos: cablesCtl?.eventosDisponibles ? cablesCtl.eventosPeriodo : null,
        resumenArboles: arbolesCtl?.resumen || null,
        resumenCables: cablesCtl?.resumen || null,
      };
      const csv = generarCsvAuditoria(comun);
      const sha256 = await sha256Hex(csv);
      const { folio, generado_at: generadoAt } = await registrarReporte({
        tipo: 'auditoria', desde: periodo.desde, hasta: periodo.hasta, sha256, instalacion,
        conteos: {
          ...resumen, declaradas: diario.declaradas, hallazgos: hallazgos.length,
          arboles: arboles ? arboles.length : null, cables: lineas ? lineas.length : null,
          evaluacionesArboles: comun.evaluaciones ? comun.evaluaciones.length : null,
          eventosCables: comun.eventos ? comun.eventos.length : null,
        },
      });
      const generadoPor = (user?.id && nombres[user.id]) || user?.email || '';
      const base = `reporte-inspecciones-${instalacion.replace(/_/g, '-')}-${formatoFolio(folio)}-${periodo.desde}_a_${periodo.hasta}`;
      const { generarPdfAuditoria } = await cargarPdf();
      const doc = await generarPdfAuditoria({
        ...comun, folio, generadoAt, generadoPor, sha256, resumen, incidentes: hallazgos, nombreCsv: `${base}.csv`,
      });
      const pdf = doc.output('blob');
      const csvBlob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      setReporte({ folio, generadoAt, generadoPor, base, pdf, csv: csvBlob });
      descargar(pdf, `${base}.pdf`);
      descargar(csvBlob, `${base}.csv`);
    } catch {
      setErrorReporte(ERROR_GENERAR);
    } finally {
      setGenerando(false);
    }
  };

  return (
    <AppShell titulo="Reportes" descripcion="Control de inspecciones, árboles y cables por instalación y período">
      <div className="space-y-4">
        <section aria-labelledby="t-generar" className="space-y-3 rounded-xl border border-border bg-card p-4">
          <h2 id="t-generar" className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Generar reporte</h2>
          <SelectorInstalacion value={instalacion} onChange={setInstalacion} />
          <PeriodoSelector value={periodo} onChange={setPeriodo} atajos={ATAJOS_REPORTE} />
          {enValidacion ? (
            // Sin checklist vigente no hay obligación de inspeccionar: un
            // reporte mostraría días "sin inspección" que no son incumplimiento.
            <p className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground" data-testid="reporte-pendiente">
              El checklist del {etiquetaInstalacion(instalacion)} todavía está en validación: el reporte se podrá generar cuando se active.
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" size="lg" className="px-4" onClick={generar} loading={generando} disabled={cargando || !!error || !fichas}>
                {!generando && <FileCheck2 size={16} className="mr-2" aria-hidden="true" />}
                {generando ? 'Generando reporte…' : 'Generar reporte'}
              </Button>
              <span className="text-xs text-muted-foreground">Descarga el PDF y el archivo para Excel (CSV).</span>
            </div>
          )}
          {errorReporte && (
            <p role="alert" className="rounded-lg bg-error/10 px-3 py-2 text-sm text-error">{errorReporte}</p>
          )}
          {reporte && (
            <div className="rounded-xl border border-success/40 bg-success/5 p-3 text-sm" data-testid="reporte-listo">
              <div className="flex items-center gap-2 font-semibold text-foreground">
                <CheckCircle2 size={16} className="text-success" aria-hidden="true" /> Reporte N.º {formatoFolio(reporte.folio)} listo
              </div>
              <div className="mt-1 text-muted-foreground">
                Generado el {fechaHoraCorta(reporte.generadoAt)}{reporte.generadoPor ? ` por ${reporte.generadoPor}` : ''}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => descargar(reporte.pdf, `${reporte.base}.pdf`)}>
                  <Download size={14} className="mr-1.5" aria-hidden="true" /> Descargar PDF
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => descargar(reporte.csv, `${reporte.base}.csv`)}>
                  <Download size={14} className="mr-1.5" aria-hidden="true" /> Descargar Excel (CSV)
                </Button>
              </div>
            </div>
          )}
        </section>

        {error ? (
          <ErrorState title="No se pudo cargar el período" message={mensajeError(error)} onRetry={cargar} />
        ) : cargando ? (
          <Cargando texto="Cargando el período…" />
        ) : (
          <>
            <h2 className="sr-only">Resumen de control del período</h2>
            <BloqueDiario dias={dias} resumen={diario} hallazgos={hallazgos} periodo={periodo} instalacion={instalacion}
              puedeMarcar={puedeMarcar} esAdmin={esAdmin}
              onMarcar={(dia) => setDialogo({ tipo: 'marcar', dia })} onQuitar={(dia) => setDialogo({ tipo: 'anular', dia })} />
            {fichas ? (
              <>
                <BloqueArboles control={arbolesCtl} error={fichas.arboles.e} />
                <BloqueCables control={cablesCtl} error={fichas.lineas.e} />
              </>
            ) : <Cargando texto="Cargando árboles y cables…" className="py-8" />}
          </>
        )}
      </div>

      <MotivoDialog open={!!dialogo} onClose={() => setDialogo(null)}
        titulo={dialogo?.tipo === 'anular' ? 'Quitar la marca «No se operó»' : `No se operó (${etiquetaInstalacion(instalacion)})`}
        confirmar={dialogo?.tipo === 'anular' ? 'Quitar marca' : 'Guardar'}
        peligro={dialogo?.tipo === 'anular'}
        etiqueta={dialogo?.tipo === 'anular' ? 'Motivo para quitar la marca' : 'Motivo (por ejemplo: cerrado por lluvia)'}
        descripcion={dialogo && (dialogo.tipo === 'anular'
          ? <>El {fechaCorta(dialogo.dia.fecha)} volverá a contar como día sin inspección. La marca queda guardada con su motivo.</>
          : <>El {fechaCorta(dialogo.dia.fecha)} quedará como día en que no se operó el {etiquetaInstalacion(instalacion)}, con su motivo. Solo un administrador puede quitar la marca.</>)}
        onConfirm={confirmarDialogo} />
    </AppShell>
  );
}
